import React from 'react';
import {useSelector} from "react-redux";
import {BRCustomizedTooltip as Tooltip} from "../accents/BRCustomizedTooltip";
import BBDataTable from "../tables/BBDataTable";
import {renderCellExpand} from "../tables/DataGridRenderCellExpand";
import {
    bbDangerousStringToDate, bbDoesUserHaveAccessToBreachRiskLevelForEntity,
    bbFindEntityWithinPortfolioOrSelf,
    bbFormattedDateString,
} from "../../../util/bbHelpers";
import InfoSharpIcon from "@material-ui/icons/InfoSharp";
import BBExternalLink from "../accents/BBExternalLink";
import Skeleton from "react-loading-skeleton";
import BBIconQuestionTriangleCaution from "../../../assets/bbCustomIcons/BBIconQuestionTriangleCaution";
import BBIconExclamationTriangleError from "../../../assets/bbCustomIcons/BBIconExclamationTriangleError";

const BRVulnerabilitiesTable = ({portfolioView = false, cycleId=null, })=>{

    const {customers, customerInView} = useSelector(({customer}) => customer);
    const {entityInView, portfolios} = useSelector(({breachrisk}) => breachrisk);
    const {authUser} = useSelector(({auth}) => auth);

    const customerIdToUse = customers ? customerInView ? customerInView : null : null;
    const customer = customers ? customers[customerIdToUse] : null;
    const myEntity = bbFindEntityWithinPortfolioOrSelf(entityInView, portfolios, customerIdToUse);

    const isTrustedAgentForThisEntity = bbDoesUserHaveAccessToBreachRiskLevelForEntity(authUser, "infoLevel3", entityInView);
    const lastCycle = myEntity
    && myEntity.cycles
    && myEntity.settings
    && myEntity.settings.last_cycle_id
        ? myEntity.cycles[myEntity.settings.last_cycle_id] : null;

    const pastReportView = cycleId && lastCycle && cycleId !== lastCycle.id;

    const cycleInView = pastReportView
    && myEntity
    && myEntity.cycles
    && myEntity.cycles[cycleId]
        ? myEntity.cycles[cycleId]
        : lastCycle;

    //techData is an object
    const techData = myEntity
    && (lastCycle || cycleInView)
        ? cycleInView.techData
        : null;

    const lastCycleDate = lastCycle && lastCycle.summary && lastCycle.summary.cycle_datetime ? bbDangerousStringToDate(lastCycle.summary.cycle_datetime) : null

    //targets is an object
    //BUT IN SOME CASES TARGETS OBJECT MAY NOT EXIST
    const targets = cycleInView
    && cycleInView.targets
        ? cycleInView.targets
        : null;


    //vulnTargetIds is an array
    // const vulnTargetIds = techData && techData.totals ? techData.totals.vuln_targets : null;

    //bulnerabilities is an array
    const vulnerabilities = techData
    && techData.totals
    && techData.totals.vulnerabilities_detail
    && Array.isArray(techData.totals.vulnerabilities_detail)
        ? techData.totals.vulnerabilities_detail
        : null;

    // const totalNumVulns = techData
    //     && techData.meta
    //         ? techData.meta.vulnerabilities_to_send
    //         : null;

    const totalNumVulns = vulnerabilities ? vulnerabilities.length : null;
    // console.log("Preparing data for vulnerabilities table. techData", techData, "lastCycle", lastCycle, "vulnTargetIds", vulnTargetIds, "totalNumVulns", totalNumVulns, "vulnerabilities", vulnerabilities, "targets", targets)

    // console.log("TROUBLESHOOTING CVE LOADER" ,vulnerabilities ? (vulnerabilities.length/totalNumVulns) * 100 : 0, vulnerabilities, vulnerabilities && vulnerabilities.length , totalNumVulns, techData, "typeofs", typeof vulnerabilities.length, typeof totalNumVulns, vulnerabilities.length/totalNumVulns);

    const title = <div id={"tourStepBRVulnerabilitiesTable"}>
                Common Vulnerabilities Detected <Tooltip large title={"Common Vulnerabilities and Exposures (CVEs) are cataloged by the NIST National Vulnerability Database. Not all vulnerabilities or weaknesses are cataloged as a CVE, and not all CVEs listed here form Threat Vectors. Your BreachRisk™ Score does not consider CVEs unless they are associated with one or more Threat Vectors."}>
                <InfoSharpIcon style={{fontSize: "smaller"}} className={``}/>
        </Tooltip>
            </div>;

    const description = <>Common Vulnerabilities and Exposures (CVEs) are important context for risk, but don't directly influence your BreachRisk™ Score unless they help form a Threat Vector. The <BBExternalLink tipText={"Visit the NVD at nvd.nist.gov"} href={"https://nvd.nist.gov/"}>National Vulnerability Database (NVD)</BBExternalLink> is the U.S. government repository of standards based vulnerability management data represented using the Security Content Automation Protocol (SCAP).</>

    const tableData = vulnerabilities && Array.isArray(vulnerabilities) ? vulnerabilities.map((object) => {

        //find the target that this CVE relates to from within the array of targets.
        const targetObj = Array.isArray(targets) ? targets.find(x => x.Id === object.target) : null;

        //TODO: Sarah, I think when they are null/undefined, it's because he either isn't going to send them - but in some cases its going to be pagewise loading from the other Targets/IPs table in tech data, so that means they will show up eventually, which means we should put a skeleton there while they are null. So i've taken a first stab at that below.

        // console.log("Vulnerabilities table, have this info to work with", object, "targetObj", targetObj, "targets", targets, "lastCycle", lastCycle);

        return{
            ...object,
            customerId: customer.CustomerId,
            targetIpAddress: targetObj && targetObj.IpAddress ? targetObj.IpAddress : null,
            targetPorts: targetObj && targetObj.Ports
                ? Array.isArray(targetObj.Ports)
                    ? targetObj.Ports.length > 0
                        ? targetObj.Ports.join(", ")
                        : "None detected"
                    : targetObj.Ports
                : "None detected",
            targetServices: targetObj && targetObj.Services ? targetObj.Services : null,
            targetFqdn: targetObj && targetObj.Fqdn ? targetObj.Fqdn : null,
            nistLink: `https://nvd.nist.gov/vuln/detail/${object.vulnerability}`,
            lastObservedDatetime: lastCycleDate,
        }
    }) : null;

    // console.log("table data for vulns table is", tableData, "*&and targets is", targets);

    const tableColumns = [
        { field: 'id', headerName: 'Threat ID', hide: true, flex : 2, sortable: true, type: 'string', renderCell: (params) => {

                return params.value}},
        { field: 'vulnerability', headerName: 'Serial', hide: false, sortable: true, type: 'string', width : 160,
            renderCell: (params) =>
                renderCellExpand(params,
                    (<BBExternalLink
                            href={params.row.nistLink}
                            tipText={`Read more about ${params.value} at the NIST National Vulnerability Database.`}>
                        {params.value}
                        </BBExternalLink>
                    ))},
        { field: 'targetIpAddress', headerName: 'IP Address', hide: false, sortable: true, type: 'string', width : 150,
            renderCell: (params) =>
                renderCellExpand(params,
                    (<Tooltip title={``}>
                          <span>
                              {params.value
                                  ? <code>
                                      {params.value}
                                  </code>
                                  : <Skeleton/>
                              }

                          </span>
                        </Tooltip>
                    ))},
        { field: 'lastObservedDatetime', headerName: 'Last Observed', hide: true, sortable: true, type: 'string', width : 130, renderCell: (params) =>
                renderCellExpand(params, (<Tooltip title={`We last observed this CVE on ${bbFormattedDateString(params.value, "long")}. The CVE may also have been present after this time.`}>
                          <span>
                              {params.value
                                  ? <span>
                                      {bbFormattedDateString(params.value, "short")}
                                  </span>
                                  : "N/A"}

                          </span>
                    </Tooltip>

                ))},
        { field: 'targetPorts', headerName: 'Ports', hide: false, sortable: true, type: 'string', flex : 2,
            renderCell: (params) =>
                renderCellExpand(params,
                    (<Tooltip title={``}>
                          <span>
                              {params.value
                                  ? params.value
                                  : <Skeleton/>
                              }
                          </span>
                        </Tooltip>
                    ))},
        { field: 'cvss2_score', headerName: 'CVSS v2', hide: false, sortable: true, type: 'string', width : 130,
            renderCell: (params) =>
                renderCellExpand(params,
                    (params.value
                                ? <BBExternalLink
                        href={'https://www.first.org/cvss/v2/faq'}
                        tipText={`Read more about the Common Vulnerability Scoring System v2 Scoring System at first.org.`}>
                        <span>
                              <strong>{params.value}</strong>
                              <small>/10</small>
                          </span>

                    </BBExternalLink>
                            : <Tooltip title={`A CVSS v2 Score is not applicable or not available for this vulnerability.`}><span> N/A </span></Tooltip>
                    ))},
        { field: 'cvss3_score', headerName: 'CVSS v3.1', hide: false, sortable: true, type: 'string', width : 130,
            renderCell: (params) =>
                renderCellExpand(params,
                    (params.value
                                ? <BBExternalLink
                                    href={'https://www.first.org/cvss/v3.1/user-guide'}
                                    tipText={`Read more about the Common Vulnerability Scoring System v3.1 Scoring System at first.org.`}>
                             <span>
                              <strong>{params.value}</strong>
                              <small>/10</small>
                          </span>

                                </BBExternalLink>
                                : <Tooltip title={`A CVSS v3.1 Score is not applicable or not available for this vulnerability.`}><span> N/A </span></Tooltip>

                    ))},
        // { field: 'nistLink', headerName: 'Details', hide: false, sortable: true, type: 'string', flex : 2,
        //     renderCell: (params) =>
        //         renderCellExpand(params,
        //             (<Tooltip title={``}>
        //                   <span>
        //
        //                   </span>
        //                 </Tooltip>
        //             ))},
        { field: 'verified', headerName: 'Verified',
            renderHeader: (params) =>
                renderCellExpand(params,
                    <div style={{lineHeight: "normal"}}>
                        <Tooltip title={"Verified: casual observation can reveal possible CVEs, but further inspection is required to verify that a CVE actually exists."}>
                    <span>
                        <BBIconExclamationTriangleError/>
                    </span>
                        </Tooltip>
                    </div>),
            hide: false, sortable: true, type: 'string', width: 75, renderCell: (params) =>
                renderCellExpand(params, (<>
                        {params.value
                            ? <Tooltip title={`This CVE has been verified: we believe it actually exists and is therefore more likely to be used by attackers in a Threat Vector.`}>
                                  <span>
                                    <BBIconExclamationTriangleError className={'text-bb-risk-high'}/>
                                  </span>
                            </Tooltip>
                            : <Tooltip title={`This CVE may be present, but we have not yet verified it. It may or may not exist.`}>
                                  <span>
                                    <BBIconQuestionTriangleCaution />
                                  </span>
                            </Tooltip>
                        }
                    </>

                ))},
    ]

    // const tableDataAsArray = () => {
    //     // console.log("Creating humansTableData as array from humanTableData ", tableData);
    //     let arr=[];
    //     for (let ob in tableData) {
    //         // console.log("ob is ", tableData[ob])
    //         arr.push(tableData[ob]);
    //     }
    //     // console.log("resulting array ", arr);
    //     return arr;
    // }

    return(
        <div>
        {(((vulnerabilities && Array.isArray(vulnerabilities)) || totalNumVulns === 0)
            // && myEntity
            // && myEntity.settings
            // && customerIdToUse
            // && !loadingPortfoliosError[customerIdToUse]
            // && !loadingPortfolios[customerIdToUse]
            // && !loadingCycles[myEntity.settings.last_cycle_id]
            // && !loadingCyclesError[myEntity.settings.last_cycle_id]
            // && !loadingCycleDetails[myEntity.settings.last_cycle_id]
            // && !loadingCycleDetailsError[myEntity.settings.last_cycle_id]
        ) ?
            <BBDataTable
                subscriptionNameOrId={portfolioView ? "BRP" : "BRfB"}
                columns={tableColumns}
                rows={tableData}
                totalRows={totalNumVulns}
                progressValue={totalNumVulns ? vulnerabilities.length / totalNumVulns * 100 : 100}
                bufferValue={totalNumVulns ? vulnerabilities.length / totalNumVulns * 100 + 10 : 100}
                title={title}
                description={description}
                enableExport
                loading={false}
                // requiresModule={"monitor"}
                requiresBRLevel = {"infoLevel3"}
                authUser = {authUser}
                entityId = {entityInView}
                customer={customer}
                rowsPerPageOptions = {[10, 25, 50, 100]}
                defaultPageSize = {10}
                lite={!portfolioView}
                // portfolioStarter={!isTrustedAgentForThisEntity}
                portfolioView={portfolioView}
                elementDisplayNameForConsoleLogs={`BRVulnerabilitiesTable`}
            />
            :
            <BBDataTable
                subscriptionNameOrId={portfolioView ? "BRP" : "BRfB"}
                columns={tableColumns}
                rows={[]}
                title={title}
                description={description}
                loading = {false}
                progressValue={0}
                bufferValue={15}
                rowsPerPageOptions = {[10, 25, 50, 100]}
                defaultPageSize = {10}
                lite={!portfolioView}
                // portfolioStarter={!isTrustedAgentForThisEntity}
                portfolioView={portfolioView}
            />
            }
        </div>
    )
}

export default BRVulnerabilitiesTable;